import type { BracketMatchup, BracketRound } from "../types/bracket";
import {
  getWorldCupGroupStageQualificationSummary,
  isWorldCupGroupStageSeriesCode,
} from "@sportex100/shared/utils/worldCupGroupStagePicks.ts";
import { canonicalRoundDisplayName } from "@sportex100/shared/utils/competitionIdentity.ts";

type MatchupPrediction = {
  winnerTeamCode: string;
  gamesWon: number;
};

export type RoundGateState = {
  roundCode: string;
  roundLabel: string;
  roundName: string;
  index: number;
  isComplete: boolean;
  isUnlocked: boolean;
  requiredRoundCode?: string;
  requiredRoundLabel?: string;
  requiredRoundName?: string;
};

type PickSatisfactionOptions = {
  requirePredictionsForCompleteMatchups?: boolean;
};

function getRoundLabel(round: BracketRound, index: number, totalRounds: number): string {
  const explicitLabels: Record<string, string> = {
    R1: "R1",
    SM: "SM",
    F: "F",
    GROUP_STAGE: "Group",
    ROUND_32: "R32",
    ROUND_16: "R16",
    QUARTER_FINAL: "QF",
    SEMI_FINAL: "SF",
    THIRD_PLACE: "3rd",
    FINAL: "Final",
    SUPER_BOWL: "Final",
  };
  const explicitLabel = explicitLabels[round.roundCode];
  if (explicitLabel) return explicitLabel;
  if (index === totalRounds - 1) return "Finals";
  const sequence = Number.isFinite(round.roundSequence) ? round.roundSequence : index + 1;
  return `R${sequence}`;
}

function getRoundName(round: BracketRound, index: number, totalRounds: number): string {
  const explicitName = canonicalRoundDisplayName(round.roundCode, round.roundName)?.trim();
  if (explicitName) return explicitName;
  if (index === totalRounds - 1) return "Finals";
  const sequence = Number.isFinite(round.roundSequence) ? round.roundSequence : index + 1;
  return `Round ${sequence}`;
}

export function isMatchupPickSatisfied(
  matchup: BracketMatchup,
  predictions: Map<string, MatchupPrediction>,
  options: PickSatisfactionOptions = {},
): boolean {
  if (matchup.status === "COMPLETE" && !options.requirePredictionsForCompleteMatchups) {
    return true;
  }
  if (!matchup.higherSeed || !matchup.lowerSeed) {
    return false;
  }
  return predictions.has(matchup.seriesCode);
}

export function isRoundComplete(
  round: BracketRound,
  predictions: Map<string, MatchupPrediction>,
  options: PickSatisfactionOptions = {},
): boolean {
  if (!round.matchups.length) {
    return false;
  }
  if (
    round.roundCode === "GROUP_STAGE" &&
    round.matchups.some((matchup) => isWorldCupGroupStageSeriesCode(matchup.seriesCode))
  ) {
    return getWorldCupGroupStageQualificationSummary(round, predictions).isComplete;
  }
  return round.matchups.every((matchup) =>
    isMatchupPickSatisfied(matchup, predictions, options),
  );
}

export function buildRoundGateState(
  visibleRounds: BracketRound[],
  predictions: Map<string, MatchupPrediction>,
  options: PickSatisfactionOptions = {},
): RoundGateState[] {
  return visibleRounds.map((round, index) => {
    const previousRound = index > 0 ? visibleRounds[index - 1] : null;
    const previousRoundLabel = previousRound
      ? getRoundLabel(previousRound, index - 1, visibleRounds.length)
      : undefined;
    const previousRoundName = previousRound
      ? getRoundName(previousRound, index - 1, visibleRounds.length)
      : undefined;
    const previousRoundComplete = previousRound
      ? isRoundComplete(previousRound, predictions, options)
      : true;

    return {
      roundCode: round.roundCode,
      roundLabel: getRoundLabel(round, index, visibleRounds.length),
      roundName: getRoundName(round, index, visibleRounds.length),
      index,
      isComplete: isRoundComplete(round, predictions, options),
      isUnlocked: index === 0 || previousRoundComplete,
      requiredRoundCode: previousRound?.roundCode,
      requiredRoundLabel: previousRoundLabel,
      requiredRoundName: previousRoundName,
    };
  });
}
